// --------------------- Import Globais ---------------------
import { useRef, useState } from "react";
import { useMediaQuery } from "@mui/material";
// --------------------- Import Antd-Design ---------------------
import {
  Col,
  Flex,
  Row,
  Button,
  Skeleton,
  Table,
  Typography,
  Form,
  Input,
  Modal,
  InputNumber,
  Space,
  Dropdown,
  Select,
} from "antd";
import type { TableProps } from "antd/es/table";
// --------------------- Import Material Icon ---------------------
import HomeIcon from "@mui/icons-material/Home";
// --------------------- Import Styles ---------------------
import { BotoesAcoes, Container } from "./styles";
// --------------------- Import Hooks ---------------------
import { useCategoriaMensagens } from "../ViewGerenciarCategoriaMensagens/hooks/useCategoriaMensagens";
// --------------------- Import Services ---------------------
import {
  atualizarCategoriaMensagemService,
  criarCategoriaMensagemService,
} from "../../services/categoriaMensagemService";

import SettingsIcon from "@mui/icons-material/Settings";
import { useNavigate } from "react-router-dom";
import type { MenuProps } from "antd";
import { validateToken } from "../../components/Globais";

import { useMensagensService } from "../../services/mensagensService";

type RodapeOption = "PREFERENCIA" | "SR";

type CategoriaMensagemRow = {
  id: number;
  nome: string;
  descricao?: string;
  validadeDias?: number;
  rodape?: RodapeOption | null;
};

export default function ViewCategoriasMensagem() {
  const { Title } = Typography;
  const isMobile = useMediaQuery("(max-width:600px)");

  const { sucesso, aviso, erro, contextHolder } = useMensagensService();

  // Hooks
  const { categorias, isLoading, fetchCategorias } = useCategoriaMensagens();

  // Modal e Form
  const [modalVisible, setModalVisible] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form] = Form.useForm();
  const categoriaId = useRef<number>(0);

  const navigate = useNavigate();

  // Abrir modal
  const openModal = (categoria?: CategoriaMensagemRow) => {
    if (categoria) {
      setEditingId(categoria.id);
      form.setFieldsValue({
        nome: categoria.nome || "",
        descricao: categoria.descricao || "",
        rodape: categoria.rodape || undefined,
        validadeDias: categoria.validadeDias || 1,
      });
    } else {
      setEditingId(null);
      form.resetFields();
    }
    setModalVisible(true);
  };

  const confirmDelete = async (_id: number) => {
    await aviso("Em desenvolvimento!");
  };

  // Salvar categoria
  const handleSave = async () => {
    const values = await form.validateFields();
    if (editingId) {
      await atualizarCategoriaMensagemService(editingId, values);
      await sucesso("Categoria de mensagem atualizada com sucesso!");
    } else {
      await criarCategoriaMensagemService(values);
      await sucesso("Categoria de mensagem criada com sucesso!");
    }
    setModalVisible(false);
    fetchCategorias();
  };

  // Dropdown menu items for actions
  const items: MenuProps["items"] = [
    { key: "1", label: "Editar" },
    { key: "2", label: "Excluir" },
    { key: "3", label: "Ver modelos" },
  ];

  const onClick: MenuProps["onClick"] = async ({ key }) => {
    const Token = validateToken();

    if (categoriaId.current === 0 || !Token) {
      await erro("Selecione um item!");
      return;
    }

    switch (key) {
      case "1": {
        const categoriaSelecionada = categorias.find(
          (cat: CategoriaMensagemRow) => cat.id === categoriaId.current
        );
        if (categoriaSelecionada) {
          openModal(categoriaSelecionada);
        } else {
          await aviso("Categoria não encontrada.");
        }
        break;
      }

      case "2":
        confirmDelete(categoriaId.current);
        break;

      case "3":
        navigate(`/gerenciarModeloMensagens?categoria=${categoriaId.current}`);
        break;

      default:
        break;
    }
  };

  // Colunas da tabela
  const columns: TableProps<CategoriaMensagemRow>["columns"] = [
    { title: "Nome", dataIndex: "nome", width: "25%", align: "center" },
    { title: "Descrição", dataIndex: "descricao", width: "40%", align: "center" },
    { title: "Rodapé", dataIndex: "rodape", width: "15%", align: "center" },
    { title: "Validade", dataIndex: "validadeDias", width: "10%", align: "center" },
    {
      title: "Ações",
      dataIndex: "operation",
      key: "operation",
      width: "10%",
      align: "center",
      render: (_, row) => (
        <Space size="middle">
          <Dropdown menu={{ items, onClick }} trigger={["click"]}>
            <BotoesAcoes
              onClick={() => {
                categoriaId.current = row.id;
              }}
            >
              <Space>
                <SettingsIcon />
              </Space>
            </BotoesAcoes>
          </Dropdown>
        </Space>
      ),
    },
  ];

  return (
    <>
      {isLoading ? (
        <Skeleton active />
      ) : (
        <Container>
          {contextHolder}
          <Row>
            <Col span={5}>
              <Flex align="center" justify="center" style={{ marginTop: 15 }}>
                <a href={`/marketing`}></a>
              </Flex>
            </Col>
            <Col span={12}>
              <Flex align="center" justify="center">
                <HomeIcon color="primary" fontSize="large" />
                <Title
                  style={{
                    marginLeft: 10,
                    marginTop: 10,
                    fontSize: isMobile ? "1rem" : "2rem",
                    color: "rgb(0, 54, 68)",
                  }}
                  level={3}
                >
                  Gerenciar Categorias de Mensagem
                </Title>
              </Flex>
            </Col>
          </Row>

          <Row>
            <Col span={24}>
              <Flex justify="flex-end" style={{ marginBottom: 16, marginRight: 60 }}>
                <Button type="primary" onClick={() => openModal()}>
                  + Categoria
                </Button>
              </Flex>

              <Flex style={{ width: "100%", overflowX: "auto" }}>
                <Table
                  rowKey="id"
                  columns={columns}
                  dataSource={categorias}
                  bordered
                  pagination={{ pageSize: 10 }}
                  style={{
                    width: "100%",
                    minWidth: 700,
                  }}
                />
              </Flex>
            </Col>
          </Row>

          <Modal
            title={editingId ? "Editar Categoria" : "Nova Categoria"}
            open={modalVisible}
            onCancel={() => setModalVisible(false)}
            onOk={handleSave}
          >
            <Form form={form} layout="vertical">
              <Form.Item name="nome" label="Nome" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="descricao" label="Descrição">
                <Input.TextArea rows={3} />
              </Form.Item>
              <Form.Item
                name="rodape"
                label="Rodapé"
                rules={[{ required: true, message: "Selecione o rodapé!" }]}
              >
                <Select
                  placeholder="Selecione o rodapé"
                  options={[
                    { label: "PREFERENCIA", value: "PREFERENCIA" },
                    { label: "SR", value: "SR" },
                  ]}
                />
              </Form.Item>
              <Form.Item
                name="validadeDias"
                label="Validade (dias)"
                rules={[{ required: true, message: "Informe a validade!" }]}
              >
                <InputNumber min={1} style={{ width: "100%" }} />
              </Form.Item>
            </Form>
          </Modal>
        </Container>
      )}
    </>
  );
}
